import React, { useEffect, useMemo, useRef } from "react";
import { pdfjs } from "react-pdf";
import usePageContext from "react-pdf/dist/esm/shared/hooks/usePageContext.js";

export const createAnnotationFreeRenderContext = (canvasContext, viewport) => ({
  annotationMode: pdfjs.AnnotationMode.DISABLE,
  canvasContext,
  viewport,
});

const AnnotationFreePdfCanvas = ({ canvasRef, onRenderSuccess }) => {
  const pageContext = usePageContext();
  const localCanvasRef = useRef(null);
  const {
    _className = "react-pdf__Page",
    devicePixelRatio = window.devicePixelRatio || 1,
    page,
    rotate,
    scale,
  } = pageContext || {};

  const renderViewport = useMemo(
    () =>
      page?.getViewport({
        scale: scale * devicePixelRatio,
        rotation: rotate,
      }),
    [devicePixelRatio, page, rotate, scale],
  );

  const viewport = useMemo(
    () => page?.getViewport({ scale, rotation: rotate }),
    [page, rotate, scale],
  );

  useEffect(() => {
    const canvas = localCanvasRef.current;
    if (!page || !canvas || !renderViewport || !viewport) return undefined;

    page.cleanup();
    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    canvas.style.visibility = "hidden";

    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return undefined;

    const renderTask = page.render(
      createAnnotationFreeRenderContext(context, renderViewport),
    );
    renderTask.promise
      .then(() => {
        canvas.style.visibility = "";
        onRenderSuccess?.();
      })
      .catch((error) => {
        if (error?.name !== "RenderingCancelledException") {
          console.error("Page render error:", error);
        }
      });

    return () => renderTask.cancel();
  }, [onRenderSuccess, page, renderViewport, viewport]);

  useEffect(
    () => () => {
      const canvas = localCanvasRef.current;
      if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
      }
    },
    [],
  );

  return (
    <canvas
      className={`${_className}__canvas`}
      dir="ltr"
      ref={(element) => {
        localCanvasRef.current = element;
        if (typeof canvasRef === "function") canvasRef(element);
        else if (canvasRef) canvasRef.current = element;
      }}
      style={{ display: "block", userSelect: "none" }}
    />
  );
};

export default AnnotationFreePdfCanvas;
